import React, { useEffect, useState } from 'react';
import { Plus, Search, Eye, PieChart, Sparkles } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useFinance } from '../context/FinanceContext';
import Skeleton from '../components/Skeleton';

export default function Home() {
  const { months, fetchRecentMonths, loading } = useFinance();
  const navigate = useNavigate();
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    fetchRecentMonths();
  }, [fetchRecentMonths]);

  const filteredMonths = months.filter(m => {
    const searchStr = `${new Date(0, m.month - 1).toLocaleString('default', { month: 'long' })} ${m.year} ${m.status}`.toLowerCase();
    return searchStr.includes(filterText.toLowerCase());
  });

  return (
    <div>
      <h2 className="text-lg font-medium text-gray-800 mb-6">Start a new month</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
        <button 
          onClick={() => navigate('/new')}
          className="py-4 px-3 border border-gray-200 rounded-3xl hover:border-blue-500 bg-white flex flex-col items-center justify-center gap-2 transition-all hover:shadow-md shadow-sm group"
        >
          <div className="w-10 h-10 rounded-full bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
            <Plus className="w-5 h-5 text-blue-600" />
          </div>
          <span className="font-medium text-xs sm:text-sm text-gray-700">Blank Month</span>
        </button>
        
        <button 
          onClick={() => navigate('/new?template=50_30_20')}
          className="py-4 px-3 border border-gray-200 rounded-3xl hover:border-indigo-500 bg-white flex flex-col items-center justify-center gap-2 transition-all hover:shadow-md shadow-sm group"
        >
          <div className="w-10 h-10 rounded-full bg-indigo-50 group-hover:bg-indigo-100 flex items-center justify-center transition-colors">
            <PieChart className="w-5 h-5 text-indigo-600" />
          </div>
          <span className="font-medium text-xs sm:text-sm text-gray-700 text-center">50/30/20 Rule</span>
        </button>

        <button 
          onClick={() => navigate('/new?template=minimalist')}
          className="py-4 px-3 border border-gray-200 rounded-3xl hover:border-teal-500 bg-white flex flex-col items-center justify-center gap-2 transition-all hover:shadow-md shadow-sm group"
        >
          <div className="w-10 h-10 rounded-full bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
            <Sparkles className="w-5 h-5 text-teal-600" />
          </div>
          <span className="font-medium text-xs sm:text-sm text-gray-700 text-center">Minimalist</span>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
        <h2 className="text-lg font-medium text-gray-800">Recent plans</h2>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input 
            type="text"
            placeholder="Search plans..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="pl-9 pr-4 py-2 border border-gray-300 rounded-full text-sm w-full sm:w-64 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <>
            {/* Mobile Skeletons */}
            <div className="md:hidden flex flex-col gap-4 p-4 bg-gray-50">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white border border-gray-200 rounded-3xl p-5 shadow-sm">
                  <div className="flex justify-between items-start mb-3">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-6 w-16" variant="circle" />
                  </div>
                  <div className="flex justify-between mb-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex justify-between mb-4">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-4 w-20" />
                  </div>
                  <div className="flex justify-end pt-3 border-t border-gray-100">
                    <Skeleton className="h-8 w-20" />
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Skeletons */}
            <div className="hidden md:block overflow-x-auto p-4">
              <div className="space-y-4">
                <div className="flex gap-4 border-b pb-2">
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                  <Skeleton className="h-5 w-1/4" />
                </div>
                {[1, 2, 3].map(i => (
                  <div key={i} className="flex gap-4 border-b pb-4 pt-2">
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-5 w-1/4" />
                    <Skeleton className="h-5 w-1/4" />
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Mobile Cards View */}
            <div className="md:hidden flex flex-col gap-3 p-3 bg-gray-50">
              {filteredMonths.map(m => (
                <div key={m.id} className="bg-white border border-gray-200 rounded-3xl shadow-sm overflow-hidden p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Link to={`/month/${m.id}`} className="font-bold text-lg text-blue-600 hover:underline">
                      {new Date(0, m.month - 1).toLocaleString('default', { month: 'long' })} {m.year}
                    </Link>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${m.status === 'Active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
                      {m.status}
                    </span>
                  </div>
                  <div className="text-gray-600 text-sm mb-1 flex justify-between">
                    <span>Income:</span>
                    <span className="font-medium text-gray-800">K{m.expected_income}</span>
                  </div>
                  <div className="text-gray-600 text-sm mb-3 flex justify-between">
                    <span>Remaining:</span>
                    <span className="font-medium text-blue-600">{m.remaining !== undefined ? `K${m.remaining}` : '--'}</span>
                  </div>
                  <div className="flex justify-end pt-2 border-t border-gray-100">
                    <Link to={`/month/${m.id}`} className="flex items-center gap-1 text-xs bg-blue-50 text-blue-600 px-3 py-1.5 rounded hover:bg-blue-100 font-medium">
                      <Eye className="w-3.5 h-3.5" /> View
                    </Link>
                  </div>
                </div>
              ))}
              {filteredMonths.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  No recent plans found.
                </div>
              )}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto p-2">
              <table className="w-full text-left border-separate border-spacing-y-3 min-w-[600px]">
                <thead>
                  <tr className="text-gray-400 text-xs uppercase tracking-wider">
                    <th className="font-bold px-6 pb-2">Month</th>
                    <th className="font-bold px-6 pb-2">Income</th>
                    <th className="font-bold px-6 pb-2">Remaining</th>
                    <th className="font-bold px-6 pb-2">Status</th>
                    <th className="font-bold px-6 pb-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMonths.map(m => (
                    <tr key={m.id} className="bg-gray-50 hover:bg-blue-50 transition-all duration-200 group">
                      <td className="px-6 py-4 rounded-l-3xl border-y border-l border-transparent group-hover:border-blue-100">
                        <Link to={`/month/${m.id}`} className="font-bold text-gray-800 group-hover:text-blue-700 transition-colors">
                          {new Date(0, m.month - 1).toLocaleString('default', { month: 'long' })} {m.year}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-gray-700 font-medium border-y border-transparent group-hover:border-blue-100">
                        K{m.expected_income}
                      </td>
                      <td className="px-6 py-4 font-bold text-blue-600 border-y border-transparent group-hover:border-blue-100">
                        {m.remaining !== undefined ? `K${m.remaining}` : '--'}
                      </td>
                      <td className="px-6 py-4 border-y border-transparent group-hover:border-blue-100">
                        <span className={`text-[10px] px-3 py-1 rounded-full font-bold uppercase tracking-wider ${m.status === 'Active' ? 'bg-green-500 text-white shadow-sm' : 'bg-gray-400 text-white shadow-sm'}`}>
                          {m.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right rounded-r-3xl border-y border-r border-transparent group-hover:border-blue-100">
                        <Link to={`/month/${m.id}`} className="inline-flex items-center gap-1 bg-white border border-gray-200 px-4 py-2 rounded-full text-sm font-medium text-blue-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-colors shadow-sm">
                          View
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {filteredMonths.length === 0 && (
                    <tr>
                      <td colSpan="5" className="p-12 text-center text-gray-500 bg-gray-50 rounded-3xl border border-dashed border-gray-300">
                        No recent plans found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
